// react-router-dom の API を Next.js (next/navigation) のシグネチャで提供する互換レイヤ。
// 完全 Next.js 撤去後の過渡期用ではなく、`@/lib/router-compat` を正規パスとして長期維持する。
//
// - useRouter(): Next.js 形式の `{ push, replace, back, forward, refresh, prefetch }` を提供
// - useParams(): react-router-dom の同名 hook (params が動的 segment と互換)
// - useSearchParams(): Next.js 形式 (URLSearchParams を直接返す) を維持
//   ※ react-router-dom の useSearchParams は [URLSearchParams, setter] のタプルを返すため変換
// - usePathname(): useLocation().pathname を返す
// - redirect / notFound: Next.js 互換の最小実装
import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from "react-router-dom";

export const useRouter = () => {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => window.location.reload(),
      prefetch: () => {
        // no-op: react-router-dom v7 では明示的な prefetch API がない
      },
    }),
    [navigate]
  );
};

export const useParams = useRRParams;

export const useSearchParams = (): URLSearchParams => {
  const [params] = useRRSearchParams();
  return params;
};

export const usePathname = () => useLocation().pathname;

export const useHash = () => useLocation().hash;

export const redirect = (href: string): never => {
  window.location.href = href;
  throw new Error(`redirect to ${href}`);
};

export const notFound = (): never => {
  throw new Error("notFound() was called");
};
